import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { MpPlace, MpRawEvent } from '@accounting/shared';
import { BackLink } from '../../components/shell/BackLink';
import { useCompanies } from '../../hooks/useCompanies';
import {
  useCreateParticipationPlace,
  useCreateParticipationToken,
  useParticipationChain,
  useParticipationPlaces,
  useParticipationRecentEvents,
  useParticipationThresholds,
  useParticipationTokens,
  useRevokeParticipationToken,
  useUpdateParticipationPlace,
  useUpdateParticipationThresholds,
} from '../../hooks/useParticipation';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/settings_/participation')({
  component: ParticipationSettingsPage,
});

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';
const ENDPOINT_URL = `${API_BASE_URL}/api/v1/participation/location-event`;

function errorText(error: unknown, fallback: string): string | undefined {
  if (!error) return undefined;
  if (error instanceof ApiError) {
    return (error.body as { error?: string } | null)?.error ?? fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

function formatInstant(iso: string | null): string {
  if (!iso) return 'never';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

function ParticipationSettingsPage() {
  return (
    <div>
      <BackLink to="/settings">Settings</BackLink>
      <h1 style={{ fontSize: 24, margin: '8px 0 4px' }}>Participation tracking</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13, maxWidth: 720 }}>
        Geofency on your phone posts an event each time you arrive at or leave a saved location.
        The location name decides which business gets the hours. Record-keeping tool only. Not
        tax advice.
      </p>

      <WebhookCard />
      <PlacesCard />
      <ThresholdsCard />
      <RecentEventsCard />
    </div>
  );
}

// ── Webhook ────────────────────────────────────────────────────────────────

function WebhookCard() {
  const tokens = useParticipationTokens();
  const create = useCreateParticipationToken();
  const revoke = useRevokeParticipationToken();
  const [label, setLabel] = useState('Geofency');
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const copy = (what: string, text: string) => {
    void navigator.clipboard.writeText(text).then(() => setCopied(what));
  };

  const generate = () => {
    setCopied(null);
    create.mutate({ label: label.trim() || 'Geofency' }, { onSuccess: (r) => setSecret(r.secret) });
  };

  const active = (tokens.data ?? []).filter((t) => !t.revokedAt);
  const revoked = (tokens.data ?? []).filter((t) => t.revokedAt);

  return (
    <div className="card" style={{ padding: 20, maxWidth: 820, marginBottom: 16 }}>
      <h2 style={{ fontSize: 16, marginTop: 0 }}>Webhook</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Point Geofency at this address. It only accepts location events; it cannot be used to read
        anything.
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <code style={{ wordBreak: 'break-all', fontSize: 13 }}>{ENDPOINT_URL}</code>
        <button className="btn-secondary" onClick={() => copy('url', ENDPOINT_URL)}>
          {copied === 'url' ? 'Copied' : 'Copy address'}
        </button>
      </div>

      {secret && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <span className="tag warn">shown once</span>
            <strong style={{ fontSize: 14 }}>Copy this token now. It cannot be shown again.</strong>
          </div>
          <code style={{ wordBreak: 'break-all', fontSize: 13, display: 'block', marginBottom: 12 }}>
            {secret}
          </code>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn-secondary" onClick={() => copy('secret', secret)}>
              {copied === 'secret' ? 'Copied' : 'Copy token'}
            </button>
            <button
              className="btn-secondary"
              onClick={() => copy('full', `${ENDPOINT_URL}?token=${secret}`)}
            >
              {copied === 'full' ? 'Copied' : 'Copy address with token'}
            </button>
            <button className="btn-secondary" onClick={() => setSecret(null)}>
              Done
            </button>
          </div>
          <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
            Prefer the token as the password under HTTP authentication if Geofency offers it. Use
            the address with the token only when there is no other way: anything that keeps the
            address also keeps the token.
          </p>
        </div>
      )}

      {tokens.isLoading && <p className="muted">Loading…</p>}
      {tokens.error && <p className="field-error">Failed to load tokens.</p>}

      {tokens.data && (
        <table className="table" style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Token</th>
              <th>Created</th>
              <th>Last used</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {active.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  No active token. Generate one to start receiving events.
                </td>
              </tr>
            )}
            {active.map((t) => (
              <tr key={t.id}>
                <td>
                  {t.label} <span className="muted">(ends in {t.hint})</span>
                </td>
                <td>{formatInstant(t.createdAt)}</td>
                <td>{formatInstant(t.lastUsedAt)}</td>
                <td>
                  <button
                    className="btn-danger"
                    disabled={revoke.isPending}
                    onClick={() => {
                      if (confirm(`Revoke "${t.label}"? The phone stops being able to post with it immediately.`)) {
                        revoke.mutate(t.id);
                      }
                    }}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
            {revoked.map((t) => (
              <tr key={t.id}>
                <td className="muted">
                  {t.label} (ends in {t.hint})
                </td>
                <td className="muted">{formatInstant(t.createdAt)}</td>
                <td className="muted">{formatInstant(t.lastUsedAt)}</td>
                <td>
                  <span className="tag">revoked</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={80}
          style={{ width: 200 }}
          aria-label="Token label"
        />
        <button className="btn-primary" onClick={generate} disabled={create.isPending}>
          {create.isPending ? 'Generating…' : 'Generate token'}
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          To rotate: generate a new token, update Geofency, then revoke the old one.
        </span>
      </div>
      {errorText(create.error ?? revoke.error, 'Request failed') && (
        <div className="field-error">{errorText(create.error ?? revoke.error, 'Request failed')}</div>
      )}
    </div>
  );
}

// ── Places ─────────────────────────────────────────────────────────────────

interface PlaceForm {
  id: string | null;
  companyId: string;
  name: string;
  externalPlaceName: string;
  address: string;
  minDwellMinutes: string;
  travelMinutes: string;
  defaultNote: string;
  defaultWorkCategory: string;
}

const EMPTY_PLACE: PlaceForm = {
  id: null,
  companyId: '',
  name: '',
  externalPlaceName: '',
  address: '',
  minDwellMinutes: '3',
  travelMinutes: '0',
  defaultNote: '',
  defaultWorkCategory: '',
};

function toForm(p: MpPlace): PlaceForm {
  return {
    id: p.id,
    companyId: p.companyId,
    name: p.name,
    externalPlaceName: p.externalPlaceName,
    address: p.address ?? '',
    minDwellMinutes: String(p.minDwellMinutes),
    travelMinutes: String(p.travelMinutes),
    defaultNote: p.defaultNote ?? '',
    defaultWorkCategory: p.defaultWorkCategory ?? '',
  };
}

function PlacesCard() {
  const places = useParticipationPlaces();
  const companies = useCompanies();
  const create = useCreateParticipationPlace();
  const update = useUpdateParticipationPlace();
  const [form, setForm] = useState<PlaceForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const set = <K extends keyof PlaceForm>(key: K, value: PlaceForm[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const save = () => {
    if (!form) return;
    setFormError(null);
    const dwell = Number(form.minDwellMinutes);
    if (!form.companyId) return setFormError('Choose the business this location belongs to.');
    if (!form.name.trim() || !form.externalPlaceName.trim()) {
      return setFormError('A name and the Geofency location name are both required.');
    }
    if (!Number.isInteger(dwell) || dwell < 0 || dwell > 240) {
      return setFormError('Minimum stay must be a whole number of minutes from 0 to 240.');
    }
    const travel = Number(form.travelMinutes || '0');
    if (!Number.isInteger(travel) || travel < 0 || travel > 600) {
      return setFormError('Travel must be a whole number of minutes from 0 to 600.');
    }
    const input = {
      companyId: form.companyId,
      name: form.name,
      externalPlaceName: form.externalPlaceName,
      address: form.address || null,
      minDwellMinutes: dwell,
      travelMinutes: travel,
      defaultNote: form.defaultNote || null,
      defaultWorkCategory: form.defaultWorkCategory || null,
    };
    const done = { onSuccess: () => setForm(null) };
    if (form.id) update.mutate({ placeId: form.id, input }, done);
    else create.mutate(input, done);
  };

  const pending = create.isPending || update.isPending;
  const saveError = formError ?? errorText(create.error ?? update.error, 'Save failed');

  return (
    <div className="card" style={{ padding: 20, maxWidth: 820, marginBottom: 16 }}>
      <h2 style={{ fontSize: 16, marginTop: 0 }}>Locations</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        One row per Geofency location. The Geofency name must match what the phone sends; capital
        letters do not matter. Events from a location that is not listed here are kept in the raw
        log and otherwise ignored.
      </p>

      {places.isLoading && <p className="muted">Loading…</p>}
      {places.error && <p className="field-error">Failed to load locations.</p>}

      {places.data && (
        <table className="table" style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Location</th>
              <th>Geofency name</th>
              <th>Business</th>
              <th className="right">Min stay</th>
              <th className="right">Travel</th>
              <th></th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {places.data.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  No locations yet.
                </td>
              </tr>
            )}
            {places.data.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>
                  <code style={{ fontSize: 13 }}>{p.externalPlaceName}</code>
                </td>
                <td>{p.companyName}</td>
                <td className="right">{p.minDwellMinutes} min</td>
                <td className="right">{p.travelMinutes > 0 ? p.travelMinutes + ' min' : ''}</td>
                <td>{p.isActive ? <span className="tag ok">tracking</span> : <span className="tag">off</span>}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn-secondary" onClick={() => { setFormError(null); setForm(toForm(p)); }}>
                    Edit
                  </button>{' '}
                  <button
                    className="btn-secondary"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ placeId: p.id, input: { isActive: !p.isActive } })}
                  >
                    {p.isActive ? 'Turn off' : 'Turn on'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!form && (
        <button className="btn-secondary" onClick={() => { setFormError(null); setForm(EMPTY_PLACE); }}>
          + Add location
        </button>
      )}

      {form && (
        <div style={{ marginTop: 8 }}>
          <h3 style={{ fontSize: 14 }}>{form.id ? 'Edit location' : 'New location'}</h3>
          <div className="form-grid">
            <label htmlFor="mp-company">Business</label>
            <select id="mp-company" value={form.companyId} onChange={(e) => set('companyId', e.target.value)}>
              <option value="">Choose…</option>
              {(companies.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <label htmlFor="mp-name">Name</label>
            <input id="mp-name" value={form.name} onChange={(e) => set('name', e.target.value)} maxLength={120} />

            <label htmlFor="mp-external">Geofency name</label>
            <input
              id="mp-external"
              value={form.externalPlaceName}
              onChange={(e) => set('externalPlaceName', e.target.value)}
              maxLength={200}
              placeholder="Exactly as it appears in Geofency"
            />

            <label htmlFor="mp-address">Address</label>
            <input
              id="mp-address"
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              maxLength={300}
              placeholder="(optional, printed on the log)"
            />

            <label htmlFor="mp-dwell">Minimum stay (min)</label>
            <input
              id="mp-dwell"
              type="text"
              inputMode="numeric"
              value={form.minDwellMinutes}
              onChange={(e) => set('minDwellMinutes', e.target.value)}
              style={{ width: 90 }}
            />

            <label htmlFor="mp-travel">Travel, round trip (min)</label>
            <input
              id="mp-travel"
              type="text"
              inputMode="numeric"
              value={form.travelMinutes}
              onChange={(e) => set('travelMinutes', e.target.value)}
              style={{ width: 90 }}
            />

            <label htmlFor="mp-category">Work category</label>
            <input
              id="mp-category"
              value={form.defaultWorkCategory}
              onChange={(e) => set('defaultWorkCategory', e.target.value)}
              maxLength={120}
              placeholder="(optional) e.g. Management"
            />

            <label htmlFor="mp-note">Default note</label>
            <input
              id="mp-note"
              value={form.defaultNote}
              onChange={(e) => set('defaultNote', e.target.value)}
              maxLength={500}
              placeholder="(optional) filled in on every visit"
            />
          </div>

          <p className="muted" style={{ fontSize: 12 }}>
            Travel is your estimate of the drive there and back, for example 40 for twenty minutes
            each way. It is added once per location per day, shown apart from on-site hours on every
            report, and can be changed on any single visit.
          </p>

          {saveError && <div className="field-error" style={{ marginTop: 12 }}>{saveError}</div>}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn-primary" onClick={save} disabled={pending}>
              {pending ? 'Saving…' : 'Save location'}
            </button>
            <button className="btn-secondary" onClick={() => setForm(null)} disabled={pending}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Thresholds ─────────────────────────────────────────────────────────────

function ThresholdsCard() {
  const thresholds = useParticipationThresholds();
  const update = useUpdateParticipationThresholds();
  const [timeZone, setTimeZone] = useState('');
  const [maxSessionHours, setMaxSessionHours] = useState('');
  const [clockSkewMinutes, setClockSkewMinutes] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [saved, setSaved] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (thresholds.data && !initialized) {
      setTimeZone(thresholds.data.timeZone);
      setMaxSessionHours(String(thresholds.data.maxSessionHours));
      setClockSkewMinutes(String(thresholds.data.clockSkewMinutes));
      setInitialized(true);
    }
  }, [thresholds.data, initialized]);

  const save = () => {
    setSaved(false);
    setFormError(null);
    const hours = Number(maxSessionHours);
    const skew = Number(clockSkewMinutes);
    if (!Number.isInteger(hours) || hours < 1 || hours > 48) {
      return setFormError('Longest session must be a whole number of hours from 1 to 48.');
    }
    if (!Number.isInteger(skew) || skew < 1 || skew > 240) {
      return setFormError('Clock allowance must be a whole number of minutes from 1 to 240.');
    }
    update.mutate(
      { timeZone: timeZone.trim(), maxSessionHours: hours, clockSkewMinutes: skew },
      { onSuccess: () => setSaved(true) },
    );
  };

  const saveError = formError ?? errorText(update.error, 'Save failed');

  return (
    <div className="card" style={{ padding: 20, maxWidth: 820, marginBottom: 16 }}>
      <h2 style={{ fontSize: 16, marginTop: 0 }}>Thresholds</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        The minimum stay is set per location above. These apply everywhere.
      </p>
      {thresholds.error && <p className="field-error">Failed to load thresholds.</p>}
      {thresholds.data && (
        <>
          <div className="form-grid">
            <label htmlFor="mp-tz">Business time zone</label>
            <input
              id="mp-tz"
              value={timeZone}
              onChange={(e) => setTimeZone(e.target.value)}
              placeholder="America/Chicago"
            />
            <label htmlFor="mp-max">Longest session (hours)</label>
            <input
              id="mp-max"
              type="text"
              inputMode="numeric"
              value={maxSessionHours}
              onChange={(e) => setMaxSessionHours(e.target.value)}
              style={{ width: 90 }}
            />
            <label htmlFor="mp-skew">Clock allowance (min)</label>
            <input
              id="mp-skew"
              type="text"
              inputMode="numeric"
              value={clockSkewMinutes}
              onChange={(e) => setClockSkewMinutes(e.target.value)}
              style={{ width: 90 }}
            />
          </div>
          <p className="muted" style={{ fontSize: 12 }}>
            The time zone decides which calendar day and tax year a visit falls in. A visit longer
            than the longest session is treated as a missed leave. When the phone&rsquo;s time and the
            server&rsquo;s differ by more than the allowance, the visit waits for you instead of counting.
          </p>
          {saveError && <div className="field-error">{saveError}</div>}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
            <button className="btn-primary" onClick={save} disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save thresholds'}
            </button>
            {saved && !update.isPending && <span className="tag ok">saved</span>}
          </div>
        </>
      )}
    </div>
  );
}

// ── Raw events ─────────────────────────────────────────────────────────────

function ChainBadge() {
  const { data, isLoading, error, refetch, isFetching } = useParticipationChain();
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      {isLoading && <span className="muted">Checking</span>}
      {error && <span className="tag">error: {errorText(error, 'check failed')}</span>}
      {data &&
        (data.valid ? (
          <span className="tag ok">event log verified ({data.count} events)</span>
        ) : (
          <span className="tag bad">
            event log TAMPERED at #{data.problems[0]?.sequence} ({data.problems.length} problems)
          </span>
        ))}
      <button className="btn-secondary" onClick={() => void refetch()} disabled={isFetching}>
        Re-check
      </button>
    </span>
  );
}

function prettyPayload(event: MpRawEvent): string {
  try {
    return JSON.stringify(JSON.parse(event.rawPayload), null, 2);
  } catch {
    return event.rawPayload.split('&').join('\n');
  }
}

function RecentEventsCard() {
  const events = useParticipationRecentEvents();

  return (
    <div className="card" style={{ padding: 20, maxWidth: 820 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <h2 style={{ fontSize: 16, margin: 0 }}>Last 20 events received</h2>
        <ChainBadge />
      </div>
      <p className="muted" style={{ fontSize: 13 }}>
        Exactly what the phone sent, newest first. The log can only be added to; every event
        carries the hash of the one before it, so a changed or removed event shows up above.
      </p>

      <button className="btn-secondary" onClick={() => void events.refetch()} disabled={events.isFetching}>
        {events.isFetching ? 'Refreshing…' : 'Refresh'}
      </button>

      {events.error && <p className="field-error">Failed to load events.</p>}
      {events.data && events.data.length === 0 && (
        <p className="muted">Nothing received yet. Send a test event from Geofency.</p>
      )}

      {(events.data ?? []).map((e) => (
        <div key={e.id} style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
            <strong>#{e.sequenceNumber}</strong>
            <span className={e.eventType === 'unknown' ? 'tag warn' : 'tag info'}>{e.eventType}</span>
            <span>{e.externalPlaceName ?? '(no location name)'}</span>
            {e.placeName ? (
              <span className="tag ok">{e.placeName}</span>
            ) : (
              <span className="tag">not a tracked location</span>
            )}
            {e.isDuplicate && <span className="tag warn">duplicate</span>}
            {e.isTest && <span className="tag warn">test, not counted</span>}
            <span className="muted">received {formatInstant(e.receivedAt)}</span>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Phone time: {e.deviceTime ? formatInstant(e.deviceTime) : `not readable (${e.deviceTimeRaw ?? 'none sent'})`}
            {' · '}read as {e.adapter}
            {e.contentType ? ` · ${e.contentType}` : ''}
          </div>
          <pre
            style={{
              fontSize: 12,
              margin: '8px 0 0',
              padding: 8,
              overflowX: 'auto',
              background: 'var(--bg-surface-2)',
              borderRadius: 4,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {prettyPayload(e)}
          </pre>
        </div>
      ))}
    </div>
  );
}
